import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@hpi-sox/hpstellar-core/visId';
import { ShoppingCartFilled, CheckmarkCircleFilled } from '@hpi-sox/hpstellar-veneer-icons';
import withPageComponents from '../../../page/components/with-page-components';
import { Helpers } from '../../../core';
import './savings-progress-bar.less';

const SavingsProgressBar = ({ ctoPrice, className = '', v2 }) => {
    const sliderPromo = ctoPrice?.sliderPromo?.[0];

    if (!sliderPromo) {
        return null;
    }

    const { awayAmt, thresholdAmt, discount } = sliderPromo;
    const progressPercentage = (1 - awayAmt / thresholdAmt) * 100;
    const remainingPercentage = 100 - progressPercentage;
    const status = progressPercentage >= 100 ? 'completed' : progressPercentage > 50 ? 'mid' : 'default';
    const isComplete = status === 'completed';
    const title = isComplete ? 'Congratulations!' : 'Buy more, save more!';
    const description = isComplete ? (
        <>
            You've unlocked <strong>{discount} OFF!</strong>
        </>
    ) : (
        <>
            You're just {Helpers.formatCurrency(awayAmt, undefined, true)} away from <strong>{discount} OFF!</strong>
        </>
    );
    const containerClasses = [
        'savings-progress-bar',
        `savings-progress-bar--${status}`,
        className,
        v2 ? 'savings-progress-bar--v2' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={containerClasses} role="region" aria-label="Savings progress indicator">
            <div className="savings-progress-bar__header">
                {v2 ? (
                    <>
                        <Typography variant="caption" tag="h4" weight={'700'}>
                            {title}
                        </Typography>
                        <Typography variant="caption">{description}</Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="body1" tag="h4" className="savings-progress-bar__title" weight="700">
                            {title}
                        </Typography>
                        <Typography variant="body2" className="savings-progress-bar__subtitle">
                            {description}
                        </Typography>
                    </>
                )}
            </div>

            <div className="savings-progress-bar__progress-container">
                <div className="savings-progress-bar__progress-bar">
                    <div
                        className={`savings-progress-bar__progress-fill savings-progress-bar__progress-fill--${status}`}
                        style={{ width: `calc(${progressPercentage}%)` }}
                        role="progressbar"
                        aria-valuenow={Math.round(progressPercentage)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Savings progress ${Math.round(progressPercentage)}% complete`}
                    />
                    {!isComplete && (
                        <>
                            {' '}
                            <ShoppingCartFilled size={24} color="#FFA238" />
                            <div
                                className={`savings-progress-bar__progress-fill savings-progress-bar__progress-fill--remaining`}
                                style={{ width: `calc(${remainingPercentage}%)` }}
                            />
                        </>
                    )}
                </div>
                <div className="savings-progress-bar__icon" aria-hidden="true">
                    <CheckmarkCircleFilled size={24} color={isComplete ? '#4CAF50' : 'rgba(33, 33, 33, 0.1)'} />
                </div>
            </div>
            {!isComplete && (
                <div className="savings-progress-bar__threshold">
                    <Typography
                        variant={v2 ? 'footnotes' : 'body2'}
                        className="savings-progress-bar__caption"
                        weight="400"
                    >
                        Applies to orders over {Helpers.formatCurrency(thresholdAmt, undefined, true)}
                    </Typography>
                </div>
            )}
        </div>
    );
};

SavingsProgressBar.propTypes = {
    ctoPrice: PropTypes.shape({
        BasePrice: PropTypes.number,
        PromPrice: PropTypes.number,
        sliderPromo: PropTypes.arrayOf(
            PropTypes.shape({
                awayAmt: PropTypes.string,
                thresholdAmt: PropTypes.string,
                discount: PropTypes.string,
            }),
        ),
    }),
    className: PropTypes.string,
};

SavingsProgressBar.defaultProps = {
    ctoPrice: null,
    className: '',
};

export default withPageComponents(SavingsProgressBar, {
    components: ['ctoPrice'],
});
